import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Select from "../../../components/BaseUI/Select";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { useTitle } from "../../../hooks/useTitle";
import {
  FiEdit3,
  FiTrash2,
  FiUsers,
  FiMail,
  FiPhone,
  FiMapPin,
  FiSettings,
  FiBell,
  FiPlus,
} from "react-icons/fi";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const Dealers = () => {
  const [dealers, setDealers] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [dealerInfo, setDealerInfo] = useState({});
  const [loading, setLoading] = useState(true);
  useTitle("Rovion MES - Bayiler");
  const format = useFormatValue;
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(currentUser.role, "/dealers", currentUser.pages || [], !!currentUser.fullAccess);

  const headers = [
    {
      name: translate("UI.NAME"),
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
    },
    {
      name: translate("UI.DEALER_CODE"),
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a?.code || "").localeCompare(String(b?.code || "")),
    },
    {
      name: "Email",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        String(a?.email || "").localeCompare(String(b?.email || "")),
    },
    {
      name: translate("UI.DEALER_PHONE"),
      dataType: "text",
      id: 3,
      customSorter: (a, b) =>
        String(a?.phone || "").localeCompare(String(b?.phone || "")),
    },
    {
      name: translate("UI.STATUS"),
      dataType: "text",
      id: 5,
      customSorter: (a, b) =>
        String(a?.status || "").localeCompare(String(b?.status || "")),
    },
    {
      name: translate("UI.CREATED"),
      dataType: "date",
      id: 6,
      customSorter: (a, b) =>
        new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0),
    },
    { name: translate("UI.ACTIONS"), id: 7, sortable: false },
  ];

  // Use column visibility hook
  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("dealer-list", headers);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.DEALERS") },
  ];

  useEffect(() => {
    fetchDealers();
  }, []);

  const fetchDealers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/dealers`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setDealers(response.data);
    } catch (error) {
      console.error("Bayileri getirme hatası:", error);
      setToasts([
        {
          description: "Bayiler yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDealerInfo = async (dealer) => {
    try {
      const response = await axios.get(`${API_URL}/api/dealers/${dealer._id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.data.success) {
        setDealerInfo(response.data.dealer);
        setSidebarOpened(true);
      } else {
        setToasts([
          {
            description:
              response.data.message ||
              "Bayi detayları yüklenirken bir hata oluştu.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Bayi detayı getirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Bayi detayları yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    }
  };

  const handleDealerUpdate = async () => {
    try {
      setLoading(true);

      const response = await axios.put(
        `${API_URL}/api/dealers/${dealerInfo._id}`,
        dealerInfo,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: response.data.message,
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);

        await fetchDealers();
        setSidebarOpened(false);
      } else {
        setToasts([
          {
            description:
              response.data.message || "Bayi güncellenirken bir hata oluştu.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Bayi güncelleme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Bayi güncellenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const deleteDealer = (code) => {
    const dealer = dealers?.find((dealer) => dealer.code === code);

    confirm({
      title: `${translate("UI.DELETE")} ${dealer?.name}?`,
      message:
        "Bu işlem geri alınamaz ve bayi ile ilgili tüm veriler silinecektir.",
      appearance: "danger",
      confirmButtonText: translate("UI.DELETE"),
      onConfirm: async () => {
        try {
          const response = await axios.delete(
            `${API_URL}/api/dealers/${code}`,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            },
          );

          if (response.data.success) {
            await fetchDealers();

            setToasts([
              {
                description: response.data.message,
                id: Date.now(),
                title: "Başarılı!",
                appearance: "success",
              },
            ]);
          }
        } catch (error) {
          console.error("Bayi silme hatası:", error);
          setToasts([
            {
              description:
                error.response?.data?.message ||
                "Bayi silinirken bir hata oluştu.",
              id: Date.now(),
              title: "Hata!",
              appearance: "danger",
            },
          ]);
        }
      },
    });
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setDealerInfo({});
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "active":
        return "success-subtle";
      case "inactive":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  return (
    <AppPage
      breadcrumbs={breadcrumbs}
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">{translate("UI.DEALERS")}</h2>
            </div>
            {canWrite && (
              <div className="d-flex gap-2">
                <Button
                  appearance="primary-animated"
                  onClick={() => navigate("/dealers/create")}
                  icon={<FiPlus />}
                >
                  {translate("UI.ADD_DEALER")}
                </Button>
              </div>
            )}
          </div>
        </ContainerItem>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="dealer-list"
              checkboxSelection
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {dealers.map((dealer, index) => (
                <Tr key={index} id={dealer?.code || index} data={dealer}>
                  {visibleColumns.includes(0) && <Td>{dealer.name}</Td>}
                  {visibleColumns.includes(1) && (
                    <Td>
                      <code>{dealer.code}</code>
                    </Td>
                  )}
                  {visibleColumns.includes(2) && <Td>{dealer.email}</Td>}
                  {visibleColumns.includes(3) && <Td>{dealer.phone}</Td>}
                  {visibleColumns.includes(5) && (
                    <Td>
                      <Lozenge appearance={getStatusAppearance(dealer.status)}>
                        {translate(`UI.${dealer.status?.toUpperCase()}`)}
                      </Lozenge>
                    </Td>
                  )}
                  {visibleColumns.includes(6) && (
                    <Td>{format(dealer.createdAt, "date")}</Td>
                  )}
                  {visibleColumns.includes(7) && (
                    <Td>
                      <div className="d-flex align-items-center gap-1">
                        {canWrite && (
                          <>
                            <IconButton
                              icon={<FiEdit3 style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() => handleDealerInfo(dealer)}
                            />
                            <IconButton
                              icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() => deleteDealer(dealer._id)}
                            />
                          </>
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </Table>
          </div>
        </ContainerItem>
      </Container>

      <SidebarContainer
        show={sidebarOpened}
        title={translate("UI.DEALER_DETAILS")}
        onClose={closeSidebar}
      >
        {/* Temel Bilgiler */}
        <div className="d-flex align-items-center mb-4">
          <div className="me-3">
            <div className="avatar avatar-md bg-primary-subtle rounded">
              <FiUsers
                className="text-primary mr-3"
                style={{ width: "24px", height: "24px" }}
              />
            </div>
          </div>
          <div>
            <h5 className="card-title mb-1">
              {translate("UI.DEALER_INFORMATION")}
            </h5>
            <p className="text-muted small mb-0">
              {translate("UI.DEALER_INFO_DESC")}
            </p>
          </div>
        </div>

        <Input
          type="text"
          value={dealerInfo?.name}
          label={translate("UI.NAME")}
          onChange={(value) =>
            setDealerInfo((prev) => ({ ...prev, name: value }))
          }
          prefix={<FiUsers className="text-muted" />}
          required
        />
        <Input
          type="text"
          value={dealerInfo?.code}
          label={translate("UI.DEALER_CODE")}
          onChange={(value) =>
            setDealerInfo((prev) => ({ ...prev, code: value.toUpperCase() }))
          }
          hint={translate("UI.DEALER_CODE_DESC")}
          required
        />
        <Input
          type="email"
          value={dealerInfo?.email}
          label="Email"
          onChange={(value) =>
            setDealerInfo((prev) => ({ ...prev, email: value }))
          }
          prefix={<FiMail className="text-muted" />}
          required
        />
        <Input
          type="tel"
          value={dealerInfo?.phone}
          label={translate("UI.DEALER_PHONE")}
          onChange={(value) =>
            setDealerInfo((prev) => ({ ...prev, phone: value }))
          }
          prefix={<FiPhone className="text-muted" />}
          hint={translate("UI.DEALER_PHONE_DESC")}
          required
        />
        <Input
          type="text"
          value={dealerInfo?.address}
          label={translate("UI.ADDRESS")}
          onChange={(value) =>
            setDealerInfo((prev) => ({ ...prev, address: value }))
          }
          prefix={<FiMapPin className="text-muted" />}
          required
        />

        {/* Ayarlar */}
        <div className="d-flex align-items-center mb-4 mt-5">
          <div className="me-3">
            <div className="avatar avatar-md bg-info-subtle rounded">
              <FiSettings
                className="text-info mr-3"
                style={{ width: "24px", height: "24px" }}
              />
            </div>
          </div>
          <div>
            <h5 className="card-title mb-1">{translate("UI.SETTINGS")}</h5>
            <p className="text-muted small mb-0">
              {translate("UI.DEALER_SETTINGS_DESC")}
            </p>
          </div>
        </div>

        <Input
          type="email"
          value={dealerInfo?.settings?.notificationEmail}
          label={translate("UI.NOTIFICATION_EMAIL")}
          onChange={(value) =>
            setDealerInfo((prev) => ({
              ...prev,
              settings: { ...prev.settings, notificationEmail: value },
            }))
          }
          prefix={<FiBell className="text-muted" />}
          hint={translate("UI.NOTIFICATION_EMAIL_DESC")}
        />

        <Input
          type="tel"
          value={dealerInfo?.settings?.supportPhone}
          label={translate("UI.SUPPORT_PHONE")}
          onChange={(value) =>
            setDealerInfo((prev) => ({
              ...prev,
              settings: { ...prev.settings, supportPhone: value },
            }))
          }
          prefix={<FiPhone className="text-muted" />}
          hint={translate("UI.SUPPORT_PHONE_DESC")}
        />
        <Select
          label={translate("UI.STATUS")}
          value={dealerInfo?.status}
          onChange={(value) =>
            setDealerInfo((prev) => ({ ...prev, status: value }))
          }
          options={[
            { label: translate("UI.ACTIVE"), value: "active" },
            { label: translate("UI.INACTIVE"), value: "inactive" },
          ]}
        />

        <div className="mt-4">
          <Button
            title={translate("UI.SAVE")}
            appearance="primary-animated"
            onClick={handleDealerUpdate}
            loading={loading}
          />
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(Dealers, ["admin"]);
